<script setup lang="ts">
import type { TransferVisualStatus } from '~/constants/receiptModel'
import IconAutorenew from '~icons/material-symbols/autorenew'
import TransferStatusBadge from '~/ui-blocks/Formalization/blocks/paymentModel/receivables/TransferStatusBadge.vue'

const props = withDefaults(defineProps<{
  title: string
  description?: string
  value: number
  status: TransferVisualStatus
  canDownloadReceipt?: boolean
  downloading?: boolean
}>(), {
  description: '',
  canDownloadReceipt: false,
  downloading: false,
})

const emit = defineEmits<{
  (e: 'download'): void
}>()

const { t } = useI18n()
const { formatMoney } = useMoney()

const isNegative = computed(() => props.status === 'error' || props.status === 'cancelled')
const formattedValue = computed(() => formatMoney(props.value))
const hasSecondLine = computed(() => !!props.description || props.canDownloadReceipt)

function handleDownload() {
  if (props.downloading)
    return
  emit('download')
}
</script>

<template>
  <div class="flex items-center gap-2" :data-status="status">
    <TransferStatusBadge :status="status" />

    <div class="flex flex-col flex-1 min-w-0 gap-1">
      <div class="flex items-center justify-between gap-2">
        <span class="fonts-body-medium-bold text-neutral-low-medium">
          {{ title }}
        </span>

        <s v-if="isNegative" class="transfer-row__value line-through text-brand-primary-light">
          {{ formattedValue }}
        </s>
        <span v-else class="transfer-row__value text-neutral-low-dark">
          {{ formattedValue }}
        </span>
      </div>

      <div v-if="hasSecondLine" class="flex items-center justify-between gap-2">
        <span class="fonts-body-small-regular text-neutral-low-light">
          {{ description }}
        </span>

        <button
          v-if="canDownloadReceipt"
          type="button"
          class="transfer-row__receipt"
          :disabled="downloading"
          :aria-busy="downloading"
          :aria-label="t('receipt-model.receivables.view_receipt_aria', { title })"
          @click="handleDownload"
        >
          <IconAutorenew v-if="downloading" class="animate-spin shrink-0" aria-hidden="true" />
          {{ t('receipt-model.receivables.view_receipt') }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.transfer-row__value {
  @apply fonts-body-medium-bold shrink-0 whitespace-nowrap;
}

.transfer-row__receipt {
  @apply inline-flex items-center gap-1 shrink-0 whitespace-nowrap;
  @apply fonts-body-small-bold underline text-neutral-low-dark;

  &:hover:not(:disabled) {
    @apply opacity-75;
  }

  &:disabled {
    @apply cursor-wait;
  }
}
</style>
